import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { motion } from 'motion/react'
import { store } from '@/src/stores/store'
import {
    GetPreloaderTippsDocument,
    type GetPreloaderTippsQuery,
} from '@/src/lib/graphql/generated/graphql'
import TooltipBox from './TooltipBox'

const AUTO_ADVANCE_MS = 5000
const SLIDE_DURATION_S = 0.55

type PreloaderTippRow = NonNullable<GetPreloaderTippsQuery['preloaderTipps'][number]>

function isFilledPreloaderTipp(
    entry: GetPreloaderTippsQuery['preloaderTipps'][number] | undefined,
): entry is PreloaderTippRow {
    return entry != null && Boolean(entry.Tipp)
}

const PreloaderTippCarousel = observer(function PreloaderTippCarousel() {
    const [activeIndex, setActiveIndex] = useState(0)
    const [isResetting, setIsResetting] = useState(false)
    const preloaderTippsData = store.getCachedQuery(GetPreloaderTippsDocument)
    const tipps =
        preloaderTippsData?.preloaderTipps?.filter(isFilledPreloaderTipp) ?? []
    const loopedTipps = tipps.length > 1 ? [...tipps, tipps[0]] : tipps

    useEffect(() => {
        setActiveIndex(0)
        setIsResetting(false)
    }, [tipps.length])

    useEffect(() => {
        if (tipps.length <= 1 || activeIndex === tipps.length) return

        const timeoutId = window.setTimeout(() => {
            setActiveIndex((current) => current + 1)
        }, AUTO_ADVANCE_MS)

        return () => window.clearTimeout(timeoutId)
    }, [activeIndex, tipps.length])

    useEffect(() => {
        if (tipps.length <= 1 || activeIndex !== tipps.length) return

        const resetTimeoutId = window.setTimeout(() => {
            setIsResetting(true)
            setActiveIndex(0)

            window.requestAnimationFrame(() => {
                setIsResetting(false)
            })
        }, SLIDE_DURATION_S * 1000)

        return () => window.clearTimeout(resetTimeoutId)
    }, [activeIndex, tipps.length])

    if (tipps.length === 0) {
        return null
    }

    return (
        <div className='flex flex-col items-center w-full'>
            <div className='w-full overflow-hidden'>
                <motion.div
                    className='flex w-full'
                    initial={false}
                    animate={{ x: `-${activeIndex * 100}%` }}
                    transition={
                        isResetting
                            ? { duration: 0 }
                            : {
                                duration: SLIDE_DURATION_S,
                                ease: 'easeInOut',
                            }
                    }
                >
                    {loopedTipps.map((tipp, index) => (
                        <div
                            key={`${tipp.documentId}-${index}`}
                            className='flex justify-center pr-4 stele:pr-12 pl-10 stele:pl-28 w-full shrink-0'
                        >
                            <TooltipBox icon={<img src='/images/TipIcons.png' alt='powerUps' className='min-w-20 stele:min-w-80 min-h-20 stele:min-h-80' />}>
                                {tipp.Tipp}
                            </TooltipBox>
                        </div>
                    ))}
                </motion.div>
            </div>
        </div>
    )
})

export default PreloaderTippCarousel
